import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, ArrowRight, Buildings, UsersThree, ShieldCheck, Package, ListChecks, Handshake, Receipt, CheckSquare, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const GROUP_META = {
  projects:    { label: "Projects",    icon: Buildings,  tone: "text-blue-600" },
  staff:       { label: "Staff",       icon: UsersThree, tone: "text-emerald-600" },
  compliance:  { label: "Compliance",  icon: ShieldCheck, tone: "text-amber-600" },
  assets:      { label: "Assets",      icon: Package,    tone: "text-slate-600" },
  tasks:       { label: "Tasks",       icon: ListChecks, tone: "text-blue-600" },
  procurement: { label: "Procurement", icon: Handshake,  tone: "text-violet-600" },
  invoices:    { label: "Invoices",    icon: Receipt,    tone: "text-emerald-600" },
  approvals:   { label: "Approvals",   icon: CheckSquare, tone: "text-blue-600" },
  suppliers:   { label: "Suppliers",   icon: Handshake,  tone: "text-slate-600" },
};

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Cmd/Ctrl-K opens the palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Focus the input when opened
  useEffect(() => {
    if (open) {
      setQ(""); setData(null); setHighlight(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) { setData(null); return; }
    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get("/search", { params: { q: q.trim() } });
        setData(data);
        setHighlight(0);
      } catch { setData(null); }
      finally { setBusy(false); }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const flatResults = useCallback(() => {
    if (!data?.groups) return [];
    return Object.entries(data.groups).flatMap(([gkey, items]) =>
      items.map((it) => ({ ...it, _gkey: gkey }))
    );
  }, [data]);

  const goto = (item) => {
    const link = item.link?.startsWith("/admin") ? item.link.slice(6) : item.link;
    setOpen(false);
    if (link) navigate(link);
  };

  const onKeyDown = (e) => {
    const flat = flatResults();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(flat.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flat[highlight]) goto(flat[highlight]);
    }
  };

  let flatIdx = -1;

  return (
    <>
      {/* Topbar trigger */}
      <button
        onClick={() => setOpen(true)}
        className="hidden md:flex h-9 min-w-[260px] items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-left text-sm text-slate-500 transition hover:border-slate-300 hover:bg-slate-50"
        data-testid="global-search-trigger"
      >
        <MagnifyingGlass size={14} />
        <span className="flex-1">Search projects, staff, compliance…</span>
        <kbd className="hidden lg:inline-flex h-5 items-center rounded bg-slate-100 px-1.5 font-mono-alt text-[10px] font-semibold text-slate-500">
          ⌘K
        </kbd>
      </button>
      <button
        onClick={() => setOpen(true)}
        className="flex md:hidden h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600"
        aria-label="Search"
        data-testid="global-search-mobile"
      >
        <MagnifyingGlass size={16} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-2xl p-0 overflow-hidden gap-0"
          data-testid="global-search-dialog"
        >
          <DialogTitle className="sr-only">Global search</DialogTitle>
          <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
            <MagnifyingGlass size={18} className="text-slate-400" />
            <Input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search across the entire system…"
              className="border-0 bg-transparent text-base shadow-none focus-visible:ring-0"
              data-testid="global-search-input"
            />
            {busy && <Sparkle size={14} className="animate-pulse text-[#1E5CBA]" />}
          </div>
          <div className="max-h-[60vh] overflow-y-auto" data-testid="global-search-results">
            {!q || q.trim().length < 2 ? (
              <div className="px-6 py-10 text-center text-xs text-slate-500">
                Type at least 2 characters to search.
                <div className="mt-2 font-mono-alt text-[10px] uppercase tracking-widest text-slate-400">
                  Tip: ⌘K / Ctrl-K opens search anywhere · ↑ ↓ to navigate · Enter to open · Esc to close
                </div>
              </div>
            ) : !data || data.total === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-slate-500" data-testid="global-search-empty">
                {busy ? "Searching…" : <>No matches for <strong>{q}</strong>.</>}
              </div>
            ) : (
              Object.entries(data.groups).map(([gkey, items]) => {
                const meta = GROUP_META[gkey] || { label: gkey, icon: Sparkle, tone: "text-slate-500" };
                const Icon = meta.icon;
                return (
                  <div key={gkey} className="px-2 pt-2 pb-1">
                    <div className="flex items-center gap-2 px-3 py-1.5 font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
                      <Icon size={12} className={meta.tone} />
                      <span>{meta.label}</span>
                      <span className="ml-auto">{items.length}</span>
                    </div>
                    <ul>
                      {items.map((item) => {
                        flatIdx += 1;
                        const isHi = highlight === flatIdx;
                        return (
                          <li key={`${gkey}-${item.id}`}>
                            <button
                              onClick={() => goto(item)}
                              className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition ${isHi ? "bg-blue-50" : "hover:bg-slate-50"}`}
                              data-testid={`search-result-${gkey}-${item.id}`}
                            >
                              <Icon size={14} className={meta.tone} />
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-semibold text-slate-900">{item.title}</div>
                                {(item.subtitle || item.extra) && (
                                  <div className="truncate text-xs text-slate-500">
                                    {item.subtitle}{item.extra ? ` · ${item.extra}` : ""}
                                  </div>
                                )}
                              </div>
                              {item.status && (
                                <span className="hidden sm:inline-flex font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
                                  {item.status}
                                </span>
                              )}
                              <ArrowRight size={12} className={isHi ? "text-[#1E5CBA]" : "text-slate-300"} />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
